import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { __t } from '../../i18n';
import { Button, Spinner, Text } from '../../styles';
import useBreakpoint from '../../useBreakpoint';
import VideoPlayer from '../Player/VideoPlayer';
import SimplePlayer from '../SimplePlayer/Player';

export type ShortStageProps = {
  tech?: string;
  ready: boolean;
  fileId?: string;
  /** Keeps the player unmounted, so nothing plays behind the first-run hint. */
  hold?: boolean;
  /** The source couldn't be loaded — offer a way out instead of spinning forever. */
  failed?: boolean;
  onRetry?: () => void;
  /** Only passed on mobile, where the info panel lives behind a control-bar button. */
  onShowInfo?: () => void;
  /** Advances to the next episode when the current one plays out. */
  onEnded?: () => void;
};

/** The play surface: a player behind a cover that fades away once the video is playable. */
export default function ShortStage({ tech, ready, fileId, hold, failed, onRetry, onShowInfo, onEnded }: ShortStageProps) {
  const isDesktop = useBreakpoint('md');
  const frameRef = useRef<HTMLDivElement>(null);
  const [canPlay, setCanPlay] = useState(false);

  // The player remounts per episode, so the cover has to be armed again for each one.
  useEffect(() => setCanPlay(false), [fileId]);

  // Both players render a plain <video>, so the cover can read `canplay` straight off the DOM
  // rather than having the players report it — their own loader timing stays untouched for the
  // VOD route. Media events don't bubble, hence the capture phase.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    const handleCanPlay = () => setCanPlay(true);
    frame.addEventListener('canplay', handleCanPlay, true);
    return () => frame.removeEventListener('canplay', handleCanPlay, true);
  }, []);

  const playerProps = { isShort: true, onShowInfo, onEnded };

  return (
    <Frame ref={frameRef}>
      {ready && !hold && (tech === 'mp4' ? <SimplePlayer {...playerProps} /> : <VideoPlayer {...playerProps} />)}
      {/* Held for exactly as long as the content is still loading, then dissolved. If autoplay
          was refused it lifts anyway, onto the player's poster, and a tap starts playback. */}
      <Cover visible={!canPlay}>
        {failed ? (
          <Failure>
            <Text variant="body1" align="center">{__t('player.play_err')}</Text>
            <Button variant="secondary" onClick={onRetry}>{__t('player.retry')}</Button>
          </Failure>
        ) : (
          !hold && (
            <Loader visible={!canPlay}>
              <Spinner block size={isDesktop ? 'big' : 'medium'} color="primary" />
            </Loader>
          )
        )}
      </Cover>
    </Frame>
  );
}

// Positioned so both the player (`.short-player` is `inset: 0`) and the cover resolve against
// the same box, and so the listener above has a node covering the whole play surface.
const Frame = styled.div({
  position: 'absolute',
  inset: 0,
});

// The cover is inert by design, so the one thing inside it that is clickable opts back in.
const Failure = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(4),
  pointerEvents: 'auto',
}));

// Most of the opacity drop lands in the first third and the rest trails off, so the picture
// arrives promptly but the plate is never seen to snap out of frame.
const REVEAL = 'cubic-bezier(0.33, 1, 0.68, 1)';

// Clears ahead of the plate — a spinner still dissolving over live video reads as a glitch.
const Loader = styled.div<{ visible: boolean }>(({ visible }) => ({
  display: 'flex',
  opacity: visible ? 1 : 0,
  transform: `scale(${visible ? 1 : 0.8})`,
  transition: 'opacity 180ms ease-out, transform 180ms ease-out',
  '@media (prefers-reduced-motion: reduce)': {
    transform: 'none',
    transition: 'opacity 180ms linear',
  },
}));

const Cover = styled.div<{ visible: boolean }>(({ theme, visible }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'inherit',
  // One step off the page background (Main uses background[800]), so the plate reads as the
  // player rather than dissolving into the page behind the desktop panel.
  backgroundColor: theme.palette.background[700][0],
  opacity: visible ? 1 : 0,
  // A breath of scale so the plate lifts away from the picture rather than simply thinning.
  // Viewport clips the overflow, so growing it can't expose an edge.
  transform: `scale(${visible ? 1 : 1.03})`,
  // The short delay lets the first frame actually paint before the plate starts moving, which
  // is what turns this from a crossfade into a reveal.
  transition: `opacity 480ms ${REVEAL} 90ms, transform 480ms ${REVEAL} 90ms`,
  // Never in the way of the player's own click/tap surface, fading or faded.
  pointerEvents: 'none',
  '@media (prefers-reduced-motion: reduce)': {
    transform: 'none',
    transition: 'opacity 200ms linear 90ms',
  },
}));
